'use client'

import { useMemo, useState } from 'react'
import { imageClass, ImageOverlay } from './utils'
import { AtButton, Planimetry } from '../../atoms'
import { AtButtonDimensions, AtButtonTypes } from '../../types'
import { MlImages } from '../ml-images'
import { Media, OrMedia } from '../../organisms'
import dynamic from 'next/dynamic'

const DynamicMlLightboxView = dynamic(() => import('./MlLightboxView').then((mod) => mod.MlLightboxView), {
  ssr: false,
  loading: () => (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
      <div className="w-[50px] h-[50px] animate-spin rounded-full border-2 border-white border-t-transparent" />
    </div>
  ),
})

export interface MlImageGridProps {
  title?: string
  media: Media[]
  floorPlanImage?: Media
  /** Maximum amount of **secondary images** to render in the main grid, if there are more images than this value then the last image will show an overlay with a *"View all photos"* message. **Defaults to 2 secondary images** */
  maxVisible?: number
  /** Use the bigger variant of this component. See [Figma definition](https://www.figma.com/file/Awrp0dmi82JWFCXt6HGpk0/%F0%9F%A7%A9-TS-Studio---UI-Components?node-id=1136%3A12203&t=yG9ojpsB183GjtSP-1) for more details */
  large?: boolean
}

export const MlImageGrid = ({ title, media, floorPlanImage, large = false, maxVisible = 2 }: MlImageGridProps) => {
  const [open, setOpen] = useState(false)
  const [focusedImage, setFocusedImage] = useState(0)
  const slides = useMemo<any[]>(
    () =>
      (floorPlanImage ? [...media, floorPlanImage] : media)?.map((media) => {
        if (media.type === 'image') {
          return {
            ...media,
            title,
            description: media.caption,
          }
        }
        return {
          ...media,
          title,
        }
      }),
    [title, media, floorPlanImage]
  )

  const focusImage = (image: number) => () => {
    setFocusedImage(image)
    setOpen(true)
  }

  const secondaryAmount = Math.min(maxVisible, slides?.length - 1)
  const exceedsMaxVisible = slides?.length - 1 > maxVisible

  const { mainClasses, secondaryClasses } = imageClass(large)

  return (
    <>
      <div className="w-full lg:max-w-screen-lg">
        <div className="lg:grid hidden grid-rows-2 grid-flow-col gap-4">
          <div
            className={`col-span-2 row-span-2 object-cover rounded-4 cursor-pointer ${mainClasses}`}
            onClick={focusImage(0)}
          >
            <OrMedia {...slides?.[0]} videoControls={[]} />
          </div>
          {slides?.length > 1 &&
            !!maxVisible &&
            slides.slice(1, secondaryAmount + 1)?.map((slide, i) => {
              if (exceedsMaxVisible && i === secondaryAmount - 1) {
                return (
                  <ImageOverlay key={i + 1} onClick={focusImage(i + 1)} className={secondaryClasses}>
                    <OrMedia {...slide} videoControls={[]} />
                  </ImageOverlay>
                )
              }

              return (
                <div
                  key={i + 1}
                  onClick={focusImage(i + 1)}
                  className={`object-cover cursor-pointer ${secondaryClasses}`}
                >
                  <OrMedia {...slide} videoControls={[]} />
                </div>
              )
            })}
        </div>
        {/*
            //VQA REF: studio-ml-image-grid-mobile
            // TODO: remove when QA has completed their pass
        */}
        <div className="lg:hidden w-screen max-w-2xl mx-auto">
          {/* TODO: Change the name of the component to something more universal like OrSwiper and refactor */}
          <MlImages
            unsetMaxDimensions
            aspectRatio="4:3"
            media={media}
            onClick={(i) => {
              focusImage(i)()
            }}
          />
        </div>
        {floorPlanImage && (
          <div className="flex lg:flex-row-reverse pl-4 lg:pl-0 mt-4 lg:mt-2">
            <AtButton
              type={AtButtonTypes.GRAYSCALE}
              size={AtButtonDimensions.SMALL}
              title="View Floor Plan"
              IconLeftRef={Planimetry}
              onClick={focusImage(slides.length - 1)}
              parentName="MlImageGrid"
            />
          </div>
        )}
      </div>
      {open && (
        <DynamicMlLightboxView open={open} onClose={() => setOpen(false)} slides={slides} focusedImage={focusedImage} />
      )}
    </>
  )
}
